import {Component, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {Subscription} from 'rxjs';

import {AuthFacade} from '@facades/auth/authFacade';
import {School} from '@models/shool';
import {User} from '@models/user';
import {RemoveAllPrograms} from '@store/program/actions';
import * as ProgramReducer from '@store/program/reducers';
import {SignOut, UpdateAuthUser} from '@store/auth/actions';
import * as AuthReducer from '@store/auth/reducers';

@Component({
  selector: 'app-sidebar',
  templateUrl: './app-sidebar.component.html',
  styleUrls: ['./app-sidebar.component.scss']
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  user: User;
  private failedSchoolLogo = '';
  private failedUserAvatar = '';

  private authSubscription: Subscription;

  constructor(
    private authFacade: AuthFacade,
    private authStore: Store<AuthReducer.AuthState>,
    private programStore: Store<ProgramReducer.ProgramState>
  ) {}

  ngOnInit() {
    this.user = AuthFacade.getUser();
    this.authSubscription = this.authStore.subscribe((state: any) => {
      if (state.auth && state.auth.user && state.auth.user.role) {
        this.user = state.auth.user;
      }
    });
  }

  ngOnDestroy() {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  get isSuperAdmin(): boolean {
    return !!this.user && this.user.role === 'super_admin';
  }

  get isSchoolAdmin(): boolean {
    return !!this.user && this.user.role === 'admin';
  }

  get isTeacher(): boolean {
    return !!this.user && this.user.role === 'teacher';
  }

  get isStudent(): boolean {
    return !!this.user && ['student', 'student_individual', 'student_group'].includes(this.user.role);
  }

  get isSchoolContextUser(): boolean {
    return this.isSchoolAdmin || this.isTeacher || this.isStudent;
  }

  get schoolList(): School[] {
    return this.user && this.user.schools ? this.user.schools as School[] : [];
  }

  get activeSchool(): School {
    if (this.isSuperAdmin || !this.schoolList[0]) {
      return null;
    }

    const activeSchoolId = this.user.activeSchool;
    const activeSchool = activeSchoolId == null
      ? null
      : this.schoolList.find((school) => school.id != null && String(school.id) === String(activeSchoolId));
    return activeSchool || this.schoolList[0];
  }

  get hasMultipleSchools(): boolean {
    return this.schoolList.length > 1;
  }

  get schoolName(): string {
    return this.activeSchool ? this.activeSchool.name : '';
  }

  get schoolLogo(): string {
    return this.activeSchool && typeof this.activeSchool.logo === 'string'
      ? this.activeSchool.logo.trim()
      : '';
  }

  get hasSchoolLogo(): boolean {
    return !!this.schoolLogo && this.schoolLogo !== this.failedSchoolLogo;
  }

  get schoolInitials(): string {
    return this.getInitials(this.schoolName, 'SC');
  }

  get firstName(): string {
    const name = this.user && this.user.name ? this.user.name.trim() : '';
    return name ? name.split(/\s+/)[0] : 'Account';
  }

  get initial(): string {
    return this.firstName.charAt(0).toUpperCase();
  }

  get hasUserAvatar(): boolean {
    return !!this.userAvatar && this.userAvatar !== this.failedUserAvatar;
  }

  get userAvatar(): string {
    return this.user && typeof this.user.avatar === 'string' ? this.user.avatar.trim() : '';
  }

  get userInitials(): string {
    return this.getInitials(this.user && this.user.name, this.initial);
  }

  onSchoolLogoError() {
    this.failedSchoolLogo = this.schoolLogo;
  }

  onUserAvatarError() {
    this.failedUserAvatar = this.userAvatar;
  }

  useSchoolLogoFallback(event: Event) {
    const image = event.target as HTMLImageElement;
    image.onerror = null;
    image.src = 'assets/no-image.png';
  }

  private getInitials(name: string, fallback: string): string {
    const words = typeof name === 'string' ? name.trim().split(/\s+/).filter(Boolean) : [];
    if (!words.length) {
      return fallback;
    }

    return words.slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();
  }

  onSelectedSchool(school: School) {
    if (!school || school.id == null || String(school.id) === String(this.user.activeSchool)) {
      return;
    }

    this.authStore.dispatch(new UpdateAuthUser({activeSchool: school.id}));
    this.programStore.dispatch(new RemoveAllPrograms());
    window.location.reload();
  }

  onLogout() {
    this.authStore.dispatch(new SignOut(this.authFacade));
    this.programStore.dispatch(new RemoveAllPrograms());
  }
}
